import React, { useEffect, useMemo, useState } from "react";
import { Product } from "../mock/productsData";
import type { AddToCartPayload } from "./types";

interface ProductCardProps {
  product: Product;
  onAddToCart: (payload: AddToCartPayload) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const [selectedVariant, setSelectedVariant] = useState<number>(0);
  const [selectedToppings, setSelectedToppings] = useState<string[]>([]);

  const variant = product.variants ? product.variants[selectedVariant] : null;
  const displayName = variant ? `${product.name} - ${variant.name}` : product.name;
  const displayDescription = variant?.description || product.description;
  const displayIngredients = variant?.ingredients || product.ingredients;

  useEffect(() => {
    setSelectedToppings([]);
  }, [selectedVariant, product.id]);

  const saffronImage = "https://public.youware.com/image/29453864-c483-4d7d-be9b-ba20d2683f19/a8jhfnh4wi.png";
  const masticOrangeBlossomImage =
    "https://public.youware.com/image/e7e668bd-d4e9-4091-84e4-d06712615ba5/gzzs2gd9gw.png";

  const resolvedImage =
    product.name === "Saffron Faloodeh"
      ? saffronImage
      : product.name === "Mastic & Orange Blossom Faloodeh"
        ? masticOrangeBlossomImage
        : product.image === "/mastic-orange-blossom.png"
          ? masticOrangeBlossomImage
          : product.image;

  const handleToggleTopping = (toppingName: string) => {
    setSelectedToppings((prev) =>
      prev.includes(toppingName)
        ? prev.filter((name) => name !== toppingName)
        : [...prev, toppingName]
    );
  };

  const toppingsTotal = useMemo(() => {
    if (!product.toppings || selectedToppings.length === 0) return 0;

    return product.toppings
      .filter((topping) => selectedToppings.includes(topping.name))
      .reduce((sum, topping) => sum + topping.price, 0);
  }, [product.toppings, selectedToppings]);

  const priceWithToppings = useMemo(() => {
    const priceMap: Record<string, number> = {};

    for (const option of product.prices) {
      priceMap[option.size] = option.price + toppingsTotal;
    }

    return priceMap;
  }, [product.prices, toppingsTotal]);

  return (
    <div className="overflow-hidden border-2 border-transparent hover:border-secondary-300 rounded-2xl hover:shadow-2xl transition-all duration-300 bg-white">
      <div className="flex flex-col">
        <div className="relative w-full h-64">
          <img
            src={resolvedImage}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {product.badge && (
            <span className="absolute top-3 right-3 text-xs font-semibold px-2 py-1 rounded-full text-primary-900 bg-gradient-to-r from-primary-300 to-primary-400">
              Specialty
            </span>
          )}
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold mb-2 text-gray-900">{product.name}</h3>

          {/* Variant selector */}
          {product.variants && product.variants.length > 0 && (
            <div className="mb-4 space-y-2">
              {product.variants.map((variant, idx) => (
                <label key={idx} className="flex items-center p-2 border border-secondary-200 rounded-lg hover:bg-secondary-100 cursor-pointer transition-colors min-w-0">
                  <input
                    type="radio"
                    name={`variant-${product.id}`}
                    value={idx}
                    checked={selectedVariant === idx}
                    onChange={() => setSelectedVariant(idx)}
                    className="w-4 h-4 text-secondary-600 accent-secondary-600"
                  />
                  <span className="ml-3 font-medium text-gray-900 break-words">{variant.name}</span>
                </label>
              ))}
            </div>
          )}

          <p className="text-dark-600 mb-2 text-sm">{displayDescription}</p>
          <p className="text-xs text-dark-500 italic mb-4">
            Ingredients: {displayIngredients}
          </p>

          {product.toppings && product.toppings.length > 0 && (
            <div className="mb-6 rounded-xl border border-secondary-200 bg-secondary-50/60 p-4">
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-secondary-600 mb-3">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedToppings(product.toppings?.map((topping) => topping.name) || [])
                  }
                  className="hover:text-secondary-700"
                >
                  Select all syrups
                </button>
                <span className="text-dark-300">•</span>
                <button
                  type="button"
                  onClick={() => setSelectedToppings([])}
                  className="hover:text-secondary-700"
                >
                  Clear selection
                </button>
                {selectedToppings.length > 0 && (
                  <span className="text-dark-500">
                    {selectedToppings.length}
                    {selectedToppings.length === 1 ? " syrup" : " syrups"}
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-2">
                Add Syrups (+$1.99 each)
              </p>
              <div className="space-y-2">
                {product.toppings?.map((topping) => {
                  const isChecked = selectedToppings.includes(topping.name);
                  return (
                    <label
                      key={topping.name}
                      className={`flex items-center justify-between p-3 border rounded-lg transition-colors cursor-pointer ${
                        isChecked
                          ? "border-secondary-400 bg-secondary-100"
                          : "border-secondary-200 bg-white hover:bg-secondary-50"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleTopping(topping.name)}
                          className="w-4 h-4 accent-secondary-500"
                        />
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 truncate">
                            {topping.name}
                          </span>
                          {topping.description && (
                            <p className="text-xs text-dark-500 truncate">
                              {topping.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-dark-600 whitespace-nowrap">
                        ${topping.price.toFixed(2)}
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedToppings.length > 0 && (
                <div className="mt-3 rounded-lg bg-white p-3 text-xs text-dark-600">
                  <p className="font-medium text-gray-900 mb-1">Selected syrups</p>
                  <p>{selectedToppings.join(", ")}</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            {product.prices.map((option, idx) => (
              <div
                key={idx}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-secondary-100 p-3 rounded-lg"
              >
                <div>
                  <span className="font-semibold text-gray-900">{option.size}</span>
                  <span className="text-dark-700 ml-2">
                    ${priceWithToppings[option.size]?.toFixed(2) ?? option.price.toFixed(2)}
                  </span>
                  {product.toppings && selectedToppings.length > 0 && (
                    <span className="ml-2 text-xs text-secondary-600">
                      includes {selectedToppings.length} syrup{selectedToppings.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <button
                  onClick={() =>
                    onAddToCart({
                      productId: product.id,
                      name: displayName,
                      size: option.size,
                      basePrice: option.price,
                      totalPrice: priceWithToppings[option.size],
                      toppings: [...selectedToppings],
                    })
                  }
                  className="w-full sm:w-auto px-3 py-1.5 text-white rounded-md bg-gradient-to-r from-secondary-600 to-secondary-500 hover:from-secondary-700 hover:to-secondary-600 transition-all duration-200"
                >
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
